import { AnalyticsStatistic } from '@components/charts/analytics/Statistic'
import RenewalRateGauge from '@components/charts/membership/analytics-overview/RenewalRateGauge'
import {
  figureDelta,
  figureValue,
  formatInteger,
  formatPercent,
  lines,
} from '@components/charts/membership/revenue/figures'
import SectionHead from '@components/charts/membership/revenue/SectionHead'
import useRevenueStatistics from '@components/charts/membership/revenue/useRevenueStatistics'
import { ResponseMembershipRenewalRetentionAnalyticsStatistics } from '@models/membership/MembershipAnalyticsStatistics'
import { Col, Flex, Row, Typography } from 'antd'

const { Text } = Typography

const DELTA_LABEL = 'vs last period'

/** "Renewal & retention" (Figma 993:2301): renewal rate gauge + churn, over the period. */
const RenewalRetention = () => {
  const { data, isLoading, error } =
    useRevenueStatistics<ResponseMembershipRenewalRetentionAnalyticsStatistics>(
      'membership-renewal-retention',
      true
    )

  const byType = data?.renewalRateByPaymentType
  const card = figureValue(byType?.card)
  const transfer = figureValue(byType?.bank_transfer)
  const notRenewed = figureValue(data?.notRenewed)
  const voluntary = figureValue(data?.voluntary)
  const involuntary = figureValue(data?.involuntary)

  return (
    <Flex vertical gap={24}>
      <SectionHead
        title="Renewal & retention"
        caption="Follows the selected date range"
      />
      <Row gutter={[24, 24]} align="stretch">
        <Col span={12}>
          <RenewalRateGauge
            percent={figureValue(data?.renewalRate)}
            tooltip="Share of the renewals due over the period that were renewed. Cancellations and unpaid transfers count as not renewed."
            caption={
              card !== null && transfer !== null
                ? `Card ${formatPercent(card, 0)} · Bank transfer ${formatPercent(transfer, 0)}`
                : undefined
            }
          />
        </Col>
        <Col span={12}>
          <AnalyticsStatistic
            variant="overview"
            title="Churn"
            tooltip="Share of the renewals due over the period that were not renewed."
            value={formatPercent(figureValue(data?.churn), 0)}
            secondary={lines(
              notRenewed !== null &&
                voluntary !== null &&
                involuntary !== null &&
                `${formatInteger(notRenewed)} not renewed · ${formatInteger(voluntary)} cancelled · ${formatInteger(involuntary)} failed`
            )}
            delta={{
              value: figureDelta(data?.churn),
              unit: 'points',
              label: DELTA_LABEL,
              // Churn going up is a regression: red up arrow.
              higherIsBetter: false,
            }}
            isLoading={isLoading}
          />
        </Col>
      </Row>
      {error && <Text type="danger">{error}</Text>}
    </Flex>
  )
}

export default RenewalRetention
